import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowLeft, FaSave, FaShieldAlt } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import roleService from '../../services/roleService';
import './RoleManagement.css';

const ACTION_COLUMNS = ['view', 'create', 'update', 'manage'];

const formatModuleName = (moduleName) => (
  moduleName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const normalizePermissionsState = (permissions = {}) => {
  if (!permissions || typeof permissions !== 'object') {
    return {};
  }

  return Object.entries(permissions).reduce((accumulator, [key, value]) => {
    accumulator[key] = Boolean(value);
    return accumulator;
  }, {});
};

const getPermissionAliases = (permissionKey = '') => {
  const aliases = new Set([permissionKey]);

  if (permissionKey.includes('.')) {
    aliases.add(permissionKey.replace(/\./g, '_'));
  }

  if (permissionKey.includes('_')) {
    aliases.add(permissionKey.replace(/_/g, '.'));
  }

  return Array.from(aliases);
};

const isPermissionChecked = (permissionsState, permissionKey) => {
  const aliases = getPermissionAliases(permissionKey);
  return aliases.some((alias) => Boolean(permissionsState[alias]));
};

const togglePermissionState = (permissionsState, permissionKey) => {
  const aliases = getPermissionAliases(permissionKey);
  const nextValue = !aliases.some((alias) => Boolean(permissionsState[alias]));
  const nextPermissions = { ...permissionsState };

  aliases.forEach((alias) => {
    nextPermissions[alias] = nextValue;
  });

  return nextPermissions;
};

const getModuleKeyAliases = (moduleName = '') => {
  const aliases = new Set([moduleName]);

  if (moduleName) {
    aliases.add(moduleName.replace(/([a-z])([A-Z])/g, '$1_$2'));
    aliases.add(moduleName.replace(/([a-z])([A-Z])/g, '$1.$2'));
    aliases.add(moduleName.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase());
    aliases.add(moduleName.replace(/([a-z])([A-Z])/g, '$1.$2').toLowerCase());
    aliases.add(moduleName.replace(/_/g, '.'));
    aliases.add(moduleName.replace(/\./g, '_'));
  }

  return Array.from(aliases);
};

const getActionKeyAliases = (action) => {
  const aliases = new Set([action]);

  if (action === 'update') {
    aliases.add('edit');
  }

  return Array.from(aliases);
};

const findPermissionForCell = (permissions = [], moduleName, action) => {
  const moduleAliases = getModuleKeyAliases(moduleName);
  const actionAliases = getActionKeyAliases(action);

  return permissions.find((permission) => {
    const key = permission.key || '';
    return moduleAliases.some((moduleAlias) =>
      actionAliases.some((actionAlias) =>
        key === `${moduleAlias}.${actionAlias}` || key === `${moduleAlias}_${actionAlias}`
      )
    );
  }) || null;
};

const buildPermissionMatrix = (availablePermissions) => (
  Object.entries(availablePermissions).map(([moduleName, permissions]) => {
    const permissionMap = {
      view: findPermissionForCell(permissions, moduleName, 'view'),
      create: findPermissionForCell(permissions, moduleName, 'create'),
      update: findPermissionForCell(permissions, moduleName, 'update'),
      manage: findPermissionForCell(permissions, moduleName, 'manage')
    };

    return {
      moduleName,
      permissionMap
    };
  })
);

const extractResponseData = (response) => {
  if (response?.data) {
    return response.data;
  }

  return response || {};
};

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [availablePermissions, setAvailablePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    initializeForm();
  }, [id]);

  const initializeForm = async () => {
    try {
      setLoading(true);
      setError('');

      const permissionsResponse = await roleService.getAvailablePermissions();
      const permissionsData = extractResponseData(permissionsResponse);
      setAvailablePermissions(permissionsData);

      if (isEditMode) {
        const roleResponse = await roleService.getRoleById(id);
        const role = extractResponseData(roleResponse);

        setFormData({
          name: role?.name || '',
          description: role?.description || '',
          permissions: normalizePermissionsState(role?.permissions)
        });
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to load role form');
      console.error('Error loading role form:', error);
    } finally {
      setLoading(false);
    }
  };

  const permissionRows = useMemo(
    () => buildPermissionMatrix(availablePermissions),
    [availablePermissions]
  );

  const handlePermissionToggle = (permissionKey) => {
    setFormData((prev) => ({
      ...prev,
      permissions: togglePermissionState(prev.permissions, permissionKey)
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEditMode) {
        await roleService.updateRole(id, formData);
      } else {
        await roleService.createRole(formData);
      }

      navigate('/app/settings/roles', {
        state: {
          successMessage: isEditMode ? 'Role updated successfully' : 'Role created successfully'
        }
      });
    } catch (error) {
      setError(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} role`);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="role-management-container">
        <div className="loading-spinner">Loading role form...</div>
      </div>
    );
  }

  return (
    <div className="role-management-container">
      <div className="role-management-header">
        <div>
          <h1><FaShieldAlt /> {isEditMode ? 'Edit Role' : 'Create Role'}</h1>
          <p>Configure permissions by module using the matrix below</p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/app/settings/roles')}
        >
          <FaArrowLeft /> Back to Roles
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="role-form-page">
        <div className="role-form-card">
          <div className="form-group">
            <label>Role Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Project Manager"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the role's responsibilities..."
              rows="3"
            />
          </div>
        </div>

        <div className="role-form-card">
          <div className="permissions-section">
            <h3>Permissions Matrix</h3>
            <p className="permissions-hint">Hover on each checkbox cell to view the permission title and description.</p>

            <div className="permissions-table-wrapper">
              <table className="permissions-table permissions-matrix-table">
                <thead>
                  <tr>
                    <th>Module Name</th>
                    {ACTION_COLUMNS.map((action) => (
                      <th key={action} className="permission-action-header">
                        {action.charAt(0).toUpperCase() + action.slice(1)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionRows.map(({ moduleName, permissionMap }) => (
                    <tr key={moduleName}>
                      <td className="module-name-cell">{formatModuleName(moduleName)}</td>
                      {ACTION_COLUMNS.map((action) => {
                        const permission = permissionMap[action];

                        if (!permission) {
                          return (
                            <td key={`${moduleName}-${action}`} className="permission-cell permission-cell-empty">
                              —
                            </td>
                          );
                        }

                        const tooltipText = `${permission.label}${permission.description ? ` - ${permission.description}` : ''}`;

                        return (
                          <td
                            key={permission.key}
                            className="permission-cell"
                            title={tooltipText}
                          >
                            <label className="permission-matrix-checkbox" title={tooltipText}>
                              <input
                                type="checkbox"
                                checked={isPermissionChecked(formData.permissions, permission.key)}
                                onChange={() => handlePermissionToggle(permission.key)}
                              />
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {permissionRows.length === 0 && (
                    <tr>
                      <td colSpan={ACTION_COLUMNS.length + 1} className="empty-permissions-cell">
                        No permissions available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="role-form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/app/settings/roles')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            <FaSave /> {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoleForm;

// ---